import React, { useState, useMemo } from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { List, BarChart2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { MonthlyRow, InvoiceRow } from '../../../types/common';
import InvoiceModal from './InvoiceModal';

/* ───── colours (match the original) ───── */
const C_NEW    = '#3B82F6'; // blue-500
const C_REPEAT = '#0E7490'; // cyan-700
const C_LINE   = '#A855F7'; // purple-500
const C_AVG    = '#F59E0B'; // amber-500

const fmtUSD = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K`
    : `$${n.toFixed(0)}`;

const fmtUSDFull = (n: number) =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ───── Custom Tooltip ───── */
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload as MonthlyRow & { invoiced: number };
  const total = d.totalInvoiced;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-3.5 py-3 text-xs min-w-[190px]">
      <p className="font-semibold text-slate-900 mb-2">{label}</p>
      <div className="space-y-1.5">
        <div className="flex justify-between gap-4">
          <span className="text-slate-500">Total invoiced</span>
          <span className="font-semibold text-slate-900 tabular-nums">{fmtUSDFull(total)}</span>
        </div>
        {d.momPct !== null && (
          <div className="flex justify-between gap-4">
            <span className="text-slate-500">MoM %</span>
            <span className={`font-semibold tabular-nums ${d.momPct >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {d.momPct >= 0 ? '+' : ''}{d.momPct.toFixed(1)}%
            </span>
          </div>
        )}
        <div className="h-px bg-slate-100 my-1" />
        <div className="flex justify-between gap-4">
          <span style={{ color: C_NEW }}>New logo</span>
          <span className="tabular-nums" style={{ color: C_NEW }}>
            {fmtUSDFull(d.newRevenue)}
            {total > 0 && <span className="text-slate-600 ml-1">({((d.newRevenue / total) * 100).toFixed(0)}%)</span>}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span style={{ color: C_REPEAT }}>Repeat</span>
          <span className="tabular-nums" style={{ color: C_REPEAT }}>
            {fmtUSDFull(d.repeatRevenue)}
            {total > 0 && <span className="text-slate-600 ml-1">({((d.repeatRevenue / total) * 100).toFixed(0)}%)</span>}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span style={{ color: C_LINE }}>Repeat %</span>
          <span className="font-semibold tabular-nums" style={{ color: C_LINE }}>{d.repeatSharePct.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
};

/* ───── Monthly Table ───── */
type MSort = 'ym' | 'totalInvoiced' | 'newRevenue' | 'repeatRevenue' | 'repeatSharePct' | 'newClients' | 'existingClients' | 'momPct';

const MonthlyTable = ({
  rows, invoicesByYM, onMonthClick,
}: {
  rows: MonthlyRow[];
  invoicesByYM: Map<string, InvoiceRow[]>;
  onMonthClick: (ym: string) => void;
}) => {
  const [sortKey, setSortKey] = useState<MSort>('ym');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const handleSort = (k: MSort) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const av: any = a[sortKey], bv: any = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
    });
  }, [rows, sortKey, sortDir]);

  const SI = ({ k }: { k: MSort }) => {
    if (sortKey !== k) return <ArrowUpDown size={11} className="text-slate-300 ml-0.5 inline" />;
    return sortDir === 'asc'
      ? <ArrowUp size={11} className="ml-0.5 inline" style={{ color: C_LINE }} />
      : <ArrowDown size={11} className="ml-0.5 inline" style={{ color: C_LINE }} />;
  };

  const Th = ({ k, label, align = 'right', colStyle }: { k: MSort; label: string; align?: string; colStyle?: React.CSSProperties }) => (
    <th
      onClick={() => handleSort(k)}
      className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer hover:text-slate-800 whitespace-nowrap select-none text-${align}`}
      style={colStyle}
    >
      {label}<SI k={k} />
    </th>
  );

  return (
    <div className="overflow-auto max-h-[420px]">
      <table className="w-full border-collapse">
        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
          <tr>
            <Th k="ym" label="Month" align="left" />
            <Th k="totalInvoiced" label="Total Invoiced" />
            <Th k="newRevenue" label="New Logo" colStyle={{ color: C_NEW }} />
            <Th k="repeatRevenue" label="Repeat Rev" colStyle={{ color: C_REPEAT }} />
            <Th k="repeatSharePct" label="Repeat %" colStyle={{ color: C_LINE }} />
            <Th k="newClients" label="New Clients" />
            <Th k="existingClients" label="Repeat Clients" />
            <th className="px-3 py-2.5 text-center text-[10px] font-semibold uppercase tracking-wide text-slate-500">Lines</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sorted.map(row => (
            <tr key={row.ym} className="hover:bg-slate-50 group transition-colors">
              <td className="px-3 py-2.5 text-sm font-medium text-blue-600 cursor-pointer hover:underline whitespace-nowrap" onClick={() => onMonthClick(row.ym)}>
                {row.xLabel}
              </td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-800">{fmtUSDFull(row.totalInvoiced)}</td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums font-medium" style={{ color: C_NEW }}>{fmtUSDFull(row.newRevenue)}</td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums font-medium" style={{ color: C_REPEAT }}>{fmtUSDFull(row.repeatRevenue)}</td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums font-semibold" style={{ color: C_LINE }}>{row.repeatSharePct.toFixed(1)}%</td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-700">{row.newClients}</td>
              <td className="px-3 py-2.5 text-right text-sm tabular-nums text-slate-700">{row.existingClients}</td>
              <td className="px-3 py-2.5 text-center">
                <button
                  onClick={() => onMonthClick(row.ym)}
                  title="View invoice detail"
                  className="w-6 h-6 inline-flex items-center justify-center rounded text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  <List size={13} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ───── Main component ───── */
interface RevenueSectionProps {
  monthlyRows: MonthlyRow[];
  invoicesByYM: Map<string, InvoiceRow[]>;
  loading?: boolean;
}

export const RevenueSection: React.FC<RevenueSectionProps> = ({ monthlyRows, invoicesByYM, loading = false }) => {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [modalYM, setModalYM] = useState<string | null>(null);

  const avgInvoiced = useMemo(() => {
    if (!monthlyRows.length) return null;
    return monthlyRows.reduce((s, r) => s + r.totalInvoiced, 0) / monthlyRows.length;
  }, [monthlyRows]);

  const chartData = useMemo(() =>
    [...monthlyRows].sort((a, b) => a.ym.localeCompare(b.ym)),
  [monthlyRows]);

  const modalRows = useMemo(() =>
    modalYM ? (invoicesByYM.get(modalYM) ?? []) : [],
  [modalYM, invoicesByYM]);

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200">
        <h3 className="flex-1 text-sm font-semibold text-slate-900">Monthly Revenue Performance</h3>

        {/* Legend */}
        {view === 'chart' && (
          <div className="hidden sm:flex items-center gap-3 text-[11px] text-slate-500">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: C_NEW }} />New</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: C_REPEAT }} />Repeat</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 inline-block" style={{ background: C_LINE }} />Repeat %</span>
            <span className="flex items-center gap-1"><span className="w-3 h-0.5 border-t-2 border-dashed inline-block" style={{ borderColor: C_AVG }} />Avg</span>
          </div>
        )}

        {/* View toggle */}
        <div className="flex rounded-md overflow-hidden border border-slate-200">
          <button
            onClick={() => setView('chart')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${view === 'chart' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            <BarChart2 size={13} /> Chart
          </button>
          <button
            onClick={() => setView('table')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border-l border-slate-200 ${view === 'table' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
          >
            <List size={13} /> Table
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="h-[280px] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-slate-600">Loading chart data…</span>
          </div>
        </div>
      ) : view === 'chart' ? (
        <div className="p-4">
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 40, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="xLabel"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="left"
                tickFormatter={fmtUSD}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                width={60}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
                tick={{ fontSize: 10, fill: '#94a3b8' }}
                axisLine={false}
                tickLine={false}
                width={36}
              />
              <Tooltip content={<ChartTooltip />} />
              {avgInvoiced !== null && (
                <ReferenceLine
                  yAxisId="left"
                  y={avgInvoiced}
                  stroke={C_AVG}
                  strokeDasharray="4 4"
                  strokeWidth={1.5}
                />
              )}
              <Bar yAxisId="left" dataKey="newRevenue" name="New Logo" stackId="s" fill={C_NEW} radius={[0, 0, 0, 0]} maxBarSize={40} />
              <Bar yAxisId="left" dataKey="repeatRevenue" name="Repeat" stackId="s" fill={C_REPEAT} radius={[2, 2, 0, 0]} maxBarSize={40} />
              <Line
                yAxisId="right"
                dataKey="repeatSharePct"
                name="Repeat %"
                stroke={C_LINE}
                strokeWidth={2}
                dot={{ r: 2, fill: C_LINE }}
                activeDot={{ r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <MonthlyTable
          rows={monthlyRows}
          invoicesByYM={invoicesByYM}
          onMonthClick={setModalYM}
        />
      )}

      {/* Modal */}
      {modalYM && (
        <InvoiceModal
          ym={modalYM}
          rows={modalRows}
          onClose={() => setModalYM(null)}
        />
      )}
    </div>
  );
};

export default RevenueSection;
